import React, { useMemo, useRef, useState } from "react";
import { useNavigation, useTranslate } from "@refinedev/core";
import {
  Box,
  Paper,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Eye, EyeOff } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, Polygon, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import dayjs from "dayjs";
import type { UnifiedEvent, IJourney, IForbiddenZone } from "../../../types/events";
import { EVENT_COLORS, ALERT_STATUS_COLORS } from "../../../constants/eventColors";
import {
  isAlertEvent,
  isJourneyEvent,
  isForbiddenZoneEvent,
} from "../../../utils/unifiedEvents";
import { useJourneyRoutes } from "../../../hooks/useJourneyRoutes";
import { MapLayerToggles, type MapLayerVisibility } from "./MapLayerToggles";

// Leaflet CSS fix
import "leaflet/dist/leaflet.css";
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// --- Marker styles (injected once) ---
const customMarkerStyles = `
  .custom-alert-marker { background: transparent !important; border: none !important; }
  .alert-marker-content { transition: transform 0.2s ease; }
  .custom-alert-marker:hover { z-index: 1000 !important; }
  .custom-alert-marker:hover .alert-marker-content { transform: scale(1.1); }
  .alert-marker-content img { image-rendering: -webkit-optimize-contrast; image-rendering: crisp-edges; }
  @keyframes pulse { 0% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.05); opacity: 0.8; } 100% { transform: scale(1); opacity: 1; } }
  @keyframes emergencyPulse { 0% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7); } 70% { box-shadow: 0 0 0 15px rgba(255, 0, 0, 0); } 100% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0); } }
  .emergency-pulse { animation: emergencyPulse 2s infinite; }
  .alert-pulse { animation: pulse 2s ease-in-out infinite; }
`;

function createAlertMarkerHtml(
  alert: { status: string; isBroadcasting: boolean; user: { id: number; fullname: string; email: string; profilePicture?: string } },
  isActive: boolean,
) {
  const statusColors: Record<string, string> = {
    NEW: "#2196F3", ACCEPTED: "#FF9800", CALLED: "#9C27B0", CLOSED: "#4CAF50",
  };
  const statusIcons: Record<string, string> = {
    NEW: "\u{1F195}", ACCEPTED: "\u26A1", CALLED: "\u{1F4DE}", CLOSED: "\u2705",
  };
  const color = statusColors[alert.status] || "#757575";
  const pulseClass = isActive ? "emergency-pulse" : "";

  return `
    <div class="alert-marker-content" style="position: relative; width: 60px; height: 60px;">
      <div class="${pulseClass}" style="position: absolute; top: 10px; left: 10px; width: 40px; height: 40px; background: ${color}; border-radius: 50%; overflow: hidden; border: 3px solid ${color}; box-shadow: 0 2px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
        <img src="${alert.user.profilePicture || `https://api.dicebear.com/7.x/avataaars/svg?seed=${alert.user.id}`}" alt="${alert.user.fullname}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.src='https://api.dicebear.com/7.x/initials/svg?seed=${alert.user.fullname || alert.user.email}'" />
      </div>
      ${alert.status === "NEW" ? `<div style="position: absolute; top: 0; right: 0; width: 24px; height: 24px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.3); background: ${statusColors[alert.status]}; display: flex; align-items: center; justify-content: center; font-size: 16px;">${statusIcons[alert.status]}</div>` : ""}
      ${isActive ? `<div class="alert-pulse" style="position: absolute; bottom: 8px; right: 8px; width: 12px; height: 12px; background: #ff0000; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.3);"></div>` : ""}
      <div style="position: absolute; bottom: 2px; left: 50%; transform: translateX(-50%); width: 0; height: 0; border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 10px solid ${color}; filter: drop-shadow(0 2px 3px rgba(0,0,0,0.2));"></div>
    </div>`;
}

// Journey marker icons
const journeyIcons = {
  origin: L.icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
  }),
  current: L.icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
  }),
  destination: L.icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
  }),
};

// Map interaction handler — only flags genuine user interactions (drag/scroll), not programmatic moves
function MapInteractionHandler({ onInteraction, isProgrammaticRef }: { onInteraction: () => void; isProgrammaticRef: React.RefObject<boolean> }) {
  useMapEvents({
    movestart: () => { if (!isProgrammaticRef.current) onInteraction(); },
    zoomstart: () => { if (!isProgrammaticRef.current) onInteraction(); },
  });
  return null;
}

// Dynamic view updater — fits bounds to show all relevant markers, or centers on a single point
function SetViewOnChange({ bounds, center, hasUserInteracted, isProgrammaticRef }: {
  bounds: L.LatLngBounds | null;
  center: [number, number];
  hasUserInteracted: boolean;
  isProgrammaticRef: React.MutableRefObject<boolean>;
}) {
  const map = useMap();
  React.useEffect(() => {
    if (hasUserInteracted) return;
    // Flag programmatic move so MapInteractionHandler ignores it
    isProgrammaticRef.current = true;
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15, animate: true });
    } else if (center[0] !== 0 && center[1] !== 0) {
      map.setView(center, 15, { animate: true });
    }
    // Reset after animation settles
    requestAnimationFrame(() => { isProgrammaticRef.current = false; });
  }, [bounds, center, hasUserInteracted, map, isProgrammaticRef]);
  return null;
}

// --- Main Component ---

interface UnifiedMapProps {
  events: UnifiedEvent[];
  ipLocation: { latitude: number; longitude: number } | null;
  eventLocation: [number, number] | null;
  hoveredEventKey?: string | null;
  layerVisibility: MapLayerVisibility;
  onLayerChange: (layer: keyof MapLayerVisibility) => void;
  showZoneBoundaries: boolean;
  onToggleZoneBoundaries: () => void;
  alertCount: number;
  journeyCount: number;
  forbiddenZoneCount: number;
  forbiddenZoneDefinitions?: IForbiddenZone[];
}

const DEFAULT_CENTER: [number, number] = [43.2962717, 5.3832961]; // Marseille

export const UnifiedMap: React.FC<UnifiedMapProps> = ({
  events,
  ipLocation,
  eventLocation,
  hoveredEventKey,
  layerVisibility,
  onLayerChange,
  showZoneBoundaries,
  onToggleZoneBoundaries,
  alertCount,
  journeyCount,
  forbiddenZoneCount,
  forbiddenZoneDefinitions = [],
}) => {
  const translate = useTranslate();
  const { show } = useNavigation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const isProgrammaticRef = useRef(false);


  // Cache alert marker icons to avoid recreating L.divIcon on every poll (causes flickering)
  const alertIconCache = useRef<Map<string, L.DivIcon>>(new Map());

  const getAlertIcon = useMemo(() => {
    return (alert: { id: number; status: string; isBroadcasting: boolean; user: { id: number; fullname: string; email: string; profilePicture?: string } }) => {
      const cacheKey = `${alert.id}-${alert.status}-${alert.isBroadcasting}-${alert.user.profilePicture || ""}`;
      const cached = alertIconCache.current.get(cacheKey);
      if (cached) return cached;

      const icon = L.divIcon({
        html: createAlertMarkerHtml(alert, alert.isBroadcasting),
        iconSize: [60, 60],
        iconAnchor: [30, 50],
        popupAnchor: [0, -50],
        className: "custom-alert-marker",
      });
      alertIconCache.current.set(cacheKey, icon);
      return icon;
    };
  }, []);

  // Compute zone IDs with active forbidden zone entry events
  const activeZonePlaceIds = useMemo(() => {
    const ids = new Set<number>();
    for (const e of events) {
      if (isForbiddenZoneEvent(e) && e.isActive) {
        ids.add(e.data.placeId);
      }
    }
    return ids;
  }, [events]);

  // Zone definitions to display:
  // - showZoneBoundaries ON → all zones
  // - showZoneBoundaries OFF → only zones with active entry events
  const visibleZoneDefinitions = useMemo(() => {
    if (showZoneBoundaries) return forbiddenZoneDefinitions;
    return forbiddenZoneDefinitions.filter((z) => activeZonePlaceIds.has(z.id));
  }, [forbiddenZoneDefinitions, showZoneBoundaries, activeZonePlaceIds]);

  // Extract active journeys for route fetching
  const activeJourneys = useMemo<IJourney[]>(() => {
    return events
      .filter(isJourneyEvent)
      .filter((e) => e.isActive)
      .map((e) => e.data);
  }, [events]);
  const journeyRoutes = useJourneyRoutes(activeJourneys);

  // Filter visible events (hover on a card isolates that single event)
  const visibleEvents = useMemo(() => {
    return events.filter((e) => {
      if (hoveredEventKey && e.key !== hoveredEventKey) return false;
      if (isAlertEvent(e) && !layerVisibility.alerts) return false;
      if (isJourneyEvent(e) && !layerVisibility.journeys) return false;
      if (isForbiddenZoneEvent(e) && !layerVisibility.forbiddenZones) return false;
      return true;
    });
  }, [events, layerVisibility, hoveredEventKey]);

  // Events for bounds computation (ignores hover — we don't want the map jumping on hover)
  const boundsEvents = useMemo(() => {
    return events.filter((e) => {
      if (isAlertEvent(e) && !layerVisibility.alerts) return false;
      if (isJourneyEvent(e) && !layerVisibility.journeys) return false;
      if (isForbiddenZoneEvent(e) && !layerVisibility.forbiddenZones) return false;
      return true;
    });
  }, [events, layerVisibility]);

  // Compute map bounds: prioritize live events, fallback to all events
  const { mapBounds, mapCenter } = useMemo(() => {
    // Single event selected — center on it, no bounds needed
    if (eventLocation) {
      return { mapBounds: null, mapCenter: eventLocation };
    }

    const withCoords = boundsEvents.filter((e) => e.latitude && e.longitude);

    // Prioritize live (active) events for viewport
    const liveEvents = withCoords.filter((e) => e.isActive);
    const targetEvents = liveEvents.length > 0 ? liveEvents : withCoords;

    if (targetEvents.length > 0) {
      const coords = targetEvents.map((e) => [e.latitude, e.longitude] as [number, number]);
      const bounds = L.latLngBounds(coords.map(([lat, lng]) => L.latLng(lat, lng)));
      return { mapBounds: bounds, mapCenter: bounds.getCenter() as unknown as [number, number] };
    }

    const fallback: [number, number] = ipLocation
      ? [ipLocation.latitude, ipLocation.longitude]
      : DEFAULT_CENTER;
    return { mapBounds: null, mapCenter: fallback };
  }, [eventLocation, boundsEvents, ipLocation]);

  return (
    <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header with toggles */}
      <Box
        sx={{
          p: { xs: 1, md: 1.5 },
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {/* Left: title + zone boundaries pill toggle */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            onClick={onToggleZoneBoundaries}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: showZoneBoundaries ? EVENT_COLORS.forbidden_zone.main : "#9e9e9e",
              borderRadius: "20px",
              px: 0.5,
              py: 0.4,
              cursor: "pointer",
              transition: "all 0.2s ease",
              userSelect: "none",
              "&:hover": { opacity: 0.85 },
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: showZoneBoundaries ? EVENT_COLORS.forbidden_zone.main : "#9e9e9e",
                  lineHeight: 1,
                }}
              >
                {forbiddenZoneDefinitions.length}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                color: "#fff",
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              {translate("monitoring.showForbiddenZones", "Zones interdites")}
            </Typography>
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {showZoneBoundaries ? (
                <Eye size={13} color="#fff" strokeWidth={2.5} />
              ) : (
                <EyeOff size={13} color="#fff" strokeWidth={2.5} />
              )}
            </Box>
          </Box>
        </Box>

        {/* Right: event type pill toggles */}
        <MapLayerToggles
          visibility={layerVisibility}
          onChange={onLayerChange}
          alertCount={alertCount}
          journeyCount={journeyCount}
          forbiddenZoneCount={forbiddenZoneCount}
        />
      </Box>

      {/* Map */}
      <Box sx={{ flex: 1, minHeight: { xs: "300px", md: "400px" } }}>
        <style>{customMarkerStyles}</style>
        <MapContainer center={mapCenter as [number, number]} zoom={13} style={{ height: "100%", width: "100%" }}>
          {process.env.REACT_APP_STADIA_API_KEY ? (
            <TileLayer
              attribution='&copy; <a href="https://www.stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url={`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${process.env.REACT_APP_STADIA_API_KEY}`}
              minZoom={0}
              maxZoom={20}
            />
          ) : (
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              minZoom={0}
              maxZoom={19}
            />
          )}
          <SetViewOnChange bounds={mapBounds} center={mapCenter as [number, number]} hasUserInteracted={hasUserInteracted} isProgrammaticRef={isProgrammaticRef} />
          <MapInteractionHandler onInteraction={() => setHasUserInteracted(true)} isProgrammaticRef={isProgrammaticRef} />

          {/* Alert markers */}
          {visibleEvents
              .filter(isAlertEvent)
              .filter((e) => e.data.latitude && e.data.longitude)
              .map((e) => {
                const alert = e.data;
                const icon = getAlertIcon(alert);
                return (
                  <Marker key={e.key} position={[alert.latitude, alert.longitude]} icon={icon}>
                    <Popup>
                      <Box sx={{ p: 1.5, minWidth: "250px" }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                          <img
                            src={alert.user.profilePicture || `https://api.dicebear.com/7.x/avataaars/svg?seed=${alert.user.id}`}
                            alt={alert.user.fullname}
                            style={{ width: 40, height: 40, borderRadius: "50%", marginRight: 12, border: "2px solid #e0e0e0", objectFit: "cover" }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: "bold", lineHeight: 1.2, mb: 0.5 }}>
                              {alert.user.fullname || alert.user.email}
                            </Typography>
                            <Chip
                              label={translate(`monitoring.status.${alert.status.toLowerCase()}`, alert.status)}
                              size="small"
                              color={alert.status === "NEW" ? "info" : alert.status === "ACCEPTED" ? "warning" : alert.status === "CALLED" ? "secondary" : "success"}
                              sx={{ fontSize: "0.7rem", height: "20px" }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ borderTop: "1px solid #eee", my: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Typography variant="caption">
                            {dayjs(alert.createdAt).format("DD/MM/YYYY HH:mm")}
                          </Typography>
                          {alert.isBroadcasting && (
                            <Chip label={translate("monitoring.live", "Live")} color="error" size="small" sx={{ fontSize: "0.7rem", height: "20px", animation: "pulse 2s ease-in-out infinite" }} />
                          )}
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <button
                            onClick={() => show("monitoring", alert.id)}
                            style={{ width: "100%", padding: "6px 12px", backgroundColor: theme.palette.primary.main, color: "white", border: "none", borderRadius: 4, fontSize: "0.75rem", cursor: "pointer" }}
                          >
                            {translate("monitoring.viewDetails", "View Details")}
                          </button>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <a href={`https://www.google.com/maps?q=${alert.latitude},${alert.longitude}`} target="_blank" rel="noopener noreferrer" style={{ flex: 1, padding: "6px 12px", backgroundColor: "#f5f5f5", color: "#333", border: "1px solid #ddd", borderRadius: 4, fontSize: "0.75rem", textDecoration: "none", textAlign: "center" }}>
                              Maps
                            </a>
                            <a href={`https://www.google.com/maps?q=&layer=c&cbll=${alert.latitude},${alert.longitude}`} target="_blank" rel="noopener noreferrer" style={{ flex: 1, padding: "6px 12px", backgroundColor: "#f5f5f5", color: "#333", border: "1px solid #ddd", borderRadius: 4, fontSize: "0.75rem", textDecoration: "none", textAlign: "center" }}>
                              Street View
                            </a>
                          </Box>
                        </Box>
                      </Box>
                    </Popup>
                  </Marker>
                );
              })}

          {/* Journey markers + Google Maps routes */}
          {visibleEvents.filter(isJourneyEvent).map((e) => {
              const j = e.data;
              const route = journeyRoutes[j.id];
              // Fallback to straight lines if route not yet loaded
              const completedPath = route?.completed || [
                [j.originLatitude, j.originLongitude] as [number, number],
                [j.currentLatitude, j.currentLongitude] as [number, number],
              ];
              const remainingPath = route?.remaining || [
                [j.currentLatitude, j.currentLongitude] as [number, number],
                [j.destinationLatitude, j.destinationLongitude] as [number, number],
              ];

              return (
                <React.Fragment key={e.key}>
                  <Marker position={[j.originLatitude, j.originLongitude]} icon={journeyIcons.origin}>
                    <Popup>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                        {translate("monitoring.origin", "Origin")} - {j.user?.fullname}
                      </Typography>
                      <Typography variant="caption">{dayjs(j.createdAt).format("DD/MM/YYYY HH:mm")}</Typography>
                    </Popup>
                  </Marker>
                  <Marker position={[j.currentLatitude, j.currentLongitude]} icon={journeyIcons.current}>
                    <Popup>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                        {translate("monitoring.currentPosition", "Current")} - {j.user?.fullname}
                      </Typography>
                      <Typography variant="caption">
                        {translate("monitoring.lastUpdate", "Last update")}: {dayjs(j.lastHeartbeat).format("HH:mm")}
                      </Typography>
                      <br />
                      <button
                        onClick={() => window.open(`/history/journey/${j.id}`, "_blank")}
                        style={{ marginTop: 8, padding: "4px 8px", backgroundColor: theme.palette.primary.main, color: "white", border: "none", borderRadius: 4, fontSize: "0.75rem", cursor: "pointer" }}
                      >
                        {translate("monitoring.viewDetails", "View Details")}
                      </button>
                    </Popup>
                  </Marker>
                  <Marker position={[j.destinationLatitude, j.destinationLongitude]} icon={journeyIcons.destination}>
                    <Popup>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                        {translate("monitoring.destination", "Destination")} - {j.user?.fullname}
                      </Typography>
                    </Popup>
                  </Marker>
                  {/* Completed portion (origin → current): green solid */}
                  <Polyline positions={completedPath} color="#22c55e" weight={4} opacity={0.8} />
                  {/* Remaining portion (current → destination): blue dashed */}
                  <Polyline positions={remainingPath} color="#2563eb" weight={3} opacity={0.7} dashArray="10, 10" />
                </React.Fragment>
              );
            })}

          {/* Forbidden zone entry events (user entered a zone) */}
          {visibleEvents.filter(isForbiddenZoneEvent).map((e) => {
              const fz = e.data;
              const place = fz.place;
              const orangeColor = EVENT_COLORS.forbidden_zone.main;
              return (
                <React.Fragment key={e.key}>
                  <Marker position={[fz.latitude, fz.longitude]}>
                    <Popup>
                      <Box sx={{ p: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                          {place?.emoji} {place?.name}
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block" }}>
                          {fz.user?.fullname} — {dayjs(fz.enteredAt).format("DD/MM/YYYY HH:mm")}
                        </Typography>
                        {!fz.exitedAt && (
                          <Chip label={translate("monitoring.stillInside", "Still inside")} color="warning" size="small" sx={{ mt: 0.5, fontSize: "0.7rem" }} />
                        )}
                      </Box>
                    </Popup>
                  </Marker>
                </React.Fragment>
              );
            })}

          {/* Forbidden zone definitions (actual zone boundaries) */}
          {visibleZoneDefinitions.map((zone) => {
              const orangeColor = EVENT_COLORS.forbidden_zone.main;
              return (
                <React.Fragment key={`zone-def-${zone.id}`}>
                  {zone.shapeType === "POLYGON" && zone.polygon && zone.polygon.length >= 3 ? (
                    <Polygon
                      positions={zone.polygon.map((p) => [p.lat, p.lng] as [number, number])}
                      pathOptions={{
                        color: orangeColor,
                        fillColor: orangeColor,
                        fillOpacity: 0.15,
                        weight: 2,
                        dashArray: "6, 4",
                      }}
                    />
                  ) : (
                    <Circle
                      center={[zone.latitude, zone.longitude]}
                      radius={zone.radius || 100}
                      pathOptions={{
                        color: orangeColor,
                        fillColor: orangeColor,
                        fillOpacity: 0.15,
                        weight: 2,
                        dashArray: "6, 4",
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
        </MapContainer>
      </Box>
    </Paper>
  );
};
